import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import {
  usePublicPlans,
  type PublicEntitlementMeta,
  type PublicPlanCard,
  type PublicPlanMetric,
} from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { explainedPlan, FeeExplainer } from "@/components/pricing/FeeExplainer";
import { PlanRecommender } from "@/components/pricing/PlanRecommender";
import {
  ArrowIcon,
  CheckIcon,
  formatMoney,
  formatPercent,
  freeMonths,
  moneyParts,
  monthlyMinor,
  planCta,
  SectionHead,
} from "@/components/pricing/pricingFormat";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { cx } from "@/lib/utils";

import "./PricingPlans.css";

type BillingCycle = "monthly" | "annual";
type T = ReturnType<typeof useTranslation>["t"];

// KAN-1938 — „Data v EU" odebráno (rozhodnutí vlastníka 2026-10-07).
const TRUST_KEYS = ["cardVerified", "cancelAnytime"] as const;

/** KAN-1806 — odznak „kartu jen ověříme" platí jen tam, kde tarif zkoušku má. */
const hasTrial = (p: PublicPlanCard): boolean => (p.trial_days ?? 0) > 0;
const FAQ_KEYS = ["billingCycle", "transactionFee", "downgrade", "trial"] as const;

/**
 * KAN-1948 #47 — the compare-table group whose rows only an IT person reads
 * (SSO protocols, API, webhooks). It is an admin-assigned ``group`` slug like
 * any other, rendered last and folded so the shop owner is not asked to
 * decode it.
 */
const IT_GROUP = "it";

/**
 * KAN-1948 #51 — "…at most up to the monthly cap on paid plans" is only true
 * when every tier with a subscription that charges commission also caps it.
 */
const paidPlansCapped = (plans: PublicPlanCard[]): boolean => {
  const paid = plans.filter((p) => (p.price_monthly_cents ?? 0) > 0 && p.platform_fee_bps > 0);
  return paid.length > 0 && paid.every((p) => p.platform_fee_cap_monthly_minor != null);
};

/**
 * Lines of the entry card that say what EVERY tier has. ``requires`` names
 * the grant a line stands on: a platform admin can take ``contact_inbox`` or
 * the data export off a tier in /admin/plans, and the card must not keep
 * promising it.
 */
const EVERY_PLAN_BULLETS: readonly { key: string; requires?: string }[] = [
  { key: "quote" },
  { key: "orders" },
  { key: "stock" },
  { key: "invoices" },
  { key: "inbox", requires: "contact_inbox" },
  { key: "export", requires: "data.gdpr_export" },
];

/**
 * `/pricing-plans` — SaaS tier comparison for laser shop owners.
 * 100% data-driven from Platform admin (`/admin/plans`) — tier list,
 * names, prices, recommended-flag, card bullets and the detail
 * compare table all come from `GET /public/plans`. No hardcoded
 * tier→features mapping in i18n or this file.
 *
 * Layout and styling follow the Claude Design file „Cenové plány
 * (public)" (KAN-1310); the page-scoped rules are in `PricingPlans.css`.
 */
export default function PricingPlans(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  const plans = usePublicPlans();
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  // The tier the calculator picked, once the visitor has used it.
  const [calcTier, setCalcTier] = useState<string | null>(null);

  const items = plans.data?.items ?? [];

  // KAN-1947 — the meta description names the tiers the page actually
  // lists (it left Business out while the copy spelled the names).
  const tierNames = useMemo(() => {
    const names = (plans.data?.items ?? []).map((p) =>
      pickI18nBlob(p.name_i18n, p.name, p.name_en, lang),
    );
    if (!names.length) return "";
    try {
      return new Intl.ListFormat(lang, { type: "conjunction" }).format(names);
    } catch {
      return names.join(", ");
    }
  }, [plans.data, lang]);

  useSeo({
    title: t("pricingPlans.seo.title"),
    description: tierNames
      ? t("pricingPlans.seo.description", { plans: tierNames })
      : t("pricingPlans.seo.descriptionNoPlans"),
    path: "/pricing-plans",
    lang,
  });

  const entitlements = plans.data?.entitlements ?? [];
  const metrics = plans.data?.metrics ?? [];
  // KAN-1806 — věta o DPH z Profilu firmy provozovatele, ne napsaná natvrdo.
  const vatPayer = plans.data?.vat_payer ?? false;
  // KAN-1955 — měna tarifů se říká nahlas: anglický čtenář jinak neví,
  // že čísla bez symbolu jsou koruny. Z dat, ne z textu.
  const currencies = [...new Set(items.map((p) => p.currency).filter(Boolean))];
  const vatNote = [
    currencies.length > 0
      ? t("pricingPlans.currencyNote", { currency: currencies.join(", ") })
      : "",
    t(vatPayer ? "pricingPlans.vat.payer" : "pricingPlans.vat.nonPayer"),
  ]
    .filter(Boolean)
    .join(" ");
  // KAN-1948 #53 — the VAT sentence is said once: in the explainer's
  // "Vaše platba" box; without an explainer under the compare table; without
  // either under the calculator.
  const hasCompare = metrics.length > 0 || entitlements.some((e) => e.is_public_listing);
  const vatAt = explainedPlan(items) ? "explainer" : hasCompare ? "compare" : "recommender";

  // The toggle promises only what EVERY paid tier gives: the smallest
  // saving among them, and nothing when any of them saves nothing.
  const savedMonths = useMemo(() => {
    const paid = (plans.data?.items ?? []).filter(
      (p) => (p.price_monthly_cents ?? 0) > 0,
    );
    if (!paid.length) return 0;
    return Math.min(...paid.map(freeMonths));
  }, [plans.data]);

  return (
    <div className="pp">
      <section className="hero">
        <div className="grid-bg" aria-hidden="true" />
        <div className="wrap">
          <span className="eyebrow">{t("pricingPlans.eyebrow")}</span>
          <h1 className="display">
            {t("pricingPlans.titleLead")} <em>{t("pricingPlans.titleTail")}</em>
            {paidPlansCapped(items) ? (
              <span className="cap"> {t("pricingPlans.titleCap")}</span>
            ) : (
              t("pricingPlans.titleEnd")
            )}
          </h1>
          <p className="lede">{t("pricingPlans.lead")}</p>
          <div className="herobar">
            <BillingToggle
              billing={billing}
              onChange={setBilling}
              savedMonths={savedMonths}
            />
            <div className="trust">
              {TRUST_KEYS.filter(
                (k) => k !== "cardVerified" || items.some(hasTrial),
              ).map((k) => (
                <span key={k}>
                  <CheckIcon />
                  {t(`pricingPlans.trust.${k}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {plans.isLoading ? (
        <div className="wrap">
          <p className="calcfoot" data-testid="pricing-plans-loading">
            …
          </p>
        </div>
      ) : (
        <>
          <div className="wrap">
            <div
              className="plans"
              style={{ ["--pp-cols" as string]: Math.min(Math.max(items.length, 1), 4) } as CSSProperties}
              data-testid="pricing-plans-grid"
            >
              {items.map((plan, i) => (
                <PlanCardArticle
                  key={plan.tier}
                  plan={plan}
                  previous={i > 0 ? items[i - 1] : undefined}
                  entitlements={entitlements}
                  billing={billing}
                  lang={lang}
                  pickedByCalculator={plan.tier === calcTier}
                />
              ))}
            </div>

            <PlanRecommender
              plans={items}
              billing={billing}
              lang={lang}
              vatNote={vatAt === "recommender" ? vatNote : ""}
              onRecommend={setCalcTier}
            />
          </div>

          <FeeExplainer plans={items} billing={billing} lang={lang} vatNote={vatNote} />

          {hasCompare && (
            <CompareTable
              plans={items}
              entitlements={entitlements}
              metrics={metrics}
              billing={billing}
              lang={lang}
              vatNote={vatAt === "compare" ? vatNote : ""}
            />
          )}
        </>
      )}

      <section className="band alt" data-testid="pricing-plans-faq">
        <div className="wrap">
          <SectionHead
            eyebrow={t("pricingPlans.faq.eyebrow")}
            heading={t("pricingPlans.faq.heading")}
            lead={t("pricingPlans.faq.lead")}
          />
          <div className="faqgrid">
            {[FAQ_KEYS.slice(0, Math.ceil(FAQ_KEYS.length / 2)), FAQ_KEYS.slice(Math.ceil(FAQ_KEYS.length / 2))].map(
              (column, ci) => (
                <div className="faqcol" key={ci}>
                  {column.map((k) => (
                    <details className="q" key={k} open={k === FAQ_KEYS[0]}>
                      <summary>{t(`pricingPlans.faq.${k}.q`)}</summary>
                      {/* Počet dní zdarma se tu UŽ NEINTERPOLUJE: #2930
                          ho z copy vyndal (neslibovat, co nikdo nezakládá)
                          a tr02 zrušil i platformní číslo, protože
                          délka trialu je vlastnost KONKRÉTNÍHO TARIFU
                          (``Plan.trial_days``), ne jedno platformní číslo. */}
                      <p>
                        {/* KAN-1684 — měsíce zdarma z konfigurace tarifů
                            (tentýž ``savedMonths`` jako přepínač nahoře),
                            ne číslo napsané v textu. Klíče jsou zamčené. */}
                        {k === "billingCycle"
                          ? savedMonths > 0
                            ? t("pricingPlans.faq.billingCycle.a", { count: savedMonths })
                            : t("pricingPlans.faq.billingCycle.aNoDiscount")
                          : t(`pricingPlans.faq.${k}.a`)}
                      </p>
                    </details>
                  ))}
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      <section className="final">
        <div className="wrap">
          <div className="ctabox">
            <div className="grid-bg" aria-hidden="true" />
            <div>
              <h2>{t("pricingPlans.finalCta.heading")}</h2>
              <p>{t("pricingPlans.finalCta.lead")}</p>
            </div>
            <div className="ctabtns">
              <Link className="p" to={`/${lang}/sign-up-shop`}>
                {t("pricingPlans.finalCta.primary")}
              </Link>
              <Link className="s" to={`/${lang}/contact`}>
                {t("pricingPlans.finalCta.secondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ----------------------------------------------------------------------
// Billing toggle
// ----------------------------------------------------------------------

function BillingToggle({
  billing,
  onChange,
  savedMonths,
}: {
  billing: BillingCycle;
  onChange: (b: BillingCycle) => void;
  savedMonths: number;
}): JSX.Element {
  const { t } = useTranslation("saas");
  return (
    <div className="seg" data-testid="pricing-billing-toggle">
      <button
        type="button"
        aria-pressed={billing === "monthly"}
        onClick={() => onChange("monthly")}
        data-testid="pricing-billing-monthly"
      >
        {t("pricingPlans.billingMonthly")}
      </button>
      <button
        type="button"
        aria-pressed={billing === "annual"}
        onClick={() => onChange("annual")}
        data-testid="pricing-billing-annual"
      >
        {t("pricingPlans.billingAnnual")}
        {savedMonths > 0 && (
          <span className="save">
            {t("pricingPlans.annualFreeMonths", { count: savedMonths })}
          </span>
        )}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// Single plan card — keeps the per-card hooks/i18n logic isolated so
// the parent stays readable.
// ----------------------------------------------------------------------

function PlanCardArticle({
  plan,
  previous,
  entitlements,
  billing,
  lang,
  pickedByCalculator,
}: {
  plan: PublicPlanCard;
  previous: PublicPlanCard | undefined;
  entitlements: PublicEntitlementMeta[];
  billing: BillingCycle;
  lang: string;
  pickedByCalculator: boolean;
}): JSX.Element {
  const { t } = useTranslation("saas");

  const featured = plan.is_recommended;
  const name = pickI18nBlob(plan.name_i18n, plan.name, plan.name_en, lang);

  // "Vše z Pro a navíc" is only true when this tier really grants
  // everything the previous one does. The payload is not obliged to
  // nest, so it is checked, and a tier that does not nest lists its
  // full set under a plain "Obsahuje".
  const previousGrants = useMemo(
    () => (previous ? new Set(previous.default_entitlements) : null),
    [previous],
  );
  const nests =
    previousGrants != null &&
    [...previousGrants].every((k) => plan.default_entitlements.includes(k));

  // Card bullets = entitlement keys flagged is_card_highlight that
  // are also granted by this tier's default_entitlements (minus what the
  // previous tier already listed, when this one nests). Pure DB
  // projection — no per-tier logic in code.
  const cardBullets = useMemo(() => {
    const grantedSet = new Set(plan.default_entitlements);
    return entitlements
      .filter(
        (e) =>
          e.is_card_highlight &&
          grantedSet.has(e.key) &&
          !(nests && previousGrants?.has(e.key)),
      )
      .sort(
        (a, b) =>
          (a.group ?? "").localeCompare(b.group ?? "") ||
          a.display_name.localeCompare(b.display_name),
      );
  }, [entitlements, plan.default_entitlements, nests, previousGrants]);

  const cta = planCta(plan, name, lang, t);

  // What every tier has is not in the entitlement catalog — a key exists only
  // for what a plan GATES — so the entry card, built from keys alone, listed
  // one abstract bullet and sold nothing (KAN-1461). These lines are authored
  // copy; the two that a plan edit could make false are tied to the grant
  // that backs them.
  const everyPlanBullets =
    nests && previous
      ? []
      : EVERY_PLAN_BULLETS.filter(
          (bullet) => !bullet.requires || plan.default_entitlements.includes(bullet.requires),
        );

  return (
    <article
      className={cx("pc", featured && "hl", pickedByCalculator && "rec")}
      data-testid={`pricing-plans-tier-${plan.tier}`}
    >
      <span className="reclabel">{t("pricingPlans.recommendedForVolume")}</span>
      {featured && !pickedByCalculator && (
        <span className="badge-top">{t("pricingPlans.cardPopular")}</span>
      )}
      <div className="pname">{name}</div>
      <div className="ptag">
        {pickI18nBlob(plan.description_i18n, plan.description, plan.description_en, lang) || ""}
      </div>
      <PlanPrice plan={plan} billing={billing} lang={lang} />
      {/* Předplatné NENÍ celá cena — k němu se platí provize z objednávek.
          Sazba bez svého stropu se čte jako neomezená, což je přesně ta
          obava, kvůli které strop vznikl (services/platform_fee.py), takže
          se obě čísla ukazují spolu. */}
      <div className="fee" data-testid={`pricing-plans-fee-${plan.tier}`}>
        <PlatformFee plan={plan} lang={lang} />
      </div>
      <Link
        to={cta.to}
        className={cx("btn", !featured && "btn-secondary")}
        data-testid={`pricing-plans-cta-${plan.tier}`}
      >
        {cta.label}
        <ArrowIcon />
      </Link>
      <div className="inc">
        {nests && previous
          ? t("pricingPlans.includesMore", {
              plan: pickI18nBlob(previous.name_i18n, previous.name, previous.name_en, lang),
            })
          : t("pricingPlans.includes")}
      </div>
      <ul>
        {everyPlanBullets.map((bullet) => (
          <li key={bullet.key} data-testid={`pricing-plans-every-${bullet.key}`}>
            <CheckIcon />
            <span>{t(`pricingPlans.everyPlan.${bullet.key}`)}</span>
          </li>
        ))}
        {cardBullets.map((e) => {
          const marketing = pickI18nBlob(e.description_marketing_i18n, e.description_marketing, e.description_marketing_en, lang);
          return (
            <li key={e.key}>
              <CheckIcon />
              <span>
                <b>{pickI18nBlob(e.display_name_i18n, e.display_name, e.display_name_en, lang)}</b>
                {marketing && ` — ${marketing}`}
                {!e.is_wired && (
                  <span className="soon">{t("pricingPlans.comingSoon")}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

function PlanPrice({
  plan,
  billing,
  lang,
}: {
  plan: PublicPlanCard;
  billing: BillingCycle;
  lang: string;
}): JSX.Element {
  const { t } = useTranslation("saas");
  const minor = monthlyMinor(plan, billing);
  if (minor == null) {
    return (
      <div className="price">
        <b className="custom">{t("pricingPlans.contactPrice")}</b>
      </div>
    );
  }
  const { amount, currency } = moneyParts(minor, plan.currency, lang);
  const free = minor === 0;
  const unitKey = free ? "free" : billing === "annual" ? "annual" : "monthly";
  // KAN-1947 — the per-month figure of an annual plan is not what leaves
  // the account: say the sum paid up front.
  const yearly =
    !free && billing === "annual" && plan.price_annual_cents != null
      ? formatMoney(plan.price_annual_cents, plan.currency, lang)
      : null;
  return (
    <div className="price">
      <b className={free ? "zero" : undefined}>{amount}</b>
      <span className="unit">
        {t(`pricingPlans.priceUnit.${unitKey}`, { currency })}
      </span>
      {yearly && (
        <span className="yearly" data-testid={`pricing-plans-yearly-${plan.tier}`}>
          {t("pricingPlans.priceYearly", { total: yearly })}
        </span>
      )}
    </div>
  );
}

/**
 * The order take-rate under the subscription price.
 *
 * Three shapes, because the ceiling changes what the rate *means*:
 * no rate at all, an uncapped percentage, and a percentage that stops
 * at a monthly ceiling. The ceiling carries its own currency
 * (`platform_fee_cap_currency`, the platform base currency) — it is
 * deliberately not the subscription `currency`.
 */
function PlatformFee({
  plan,
  lang,
}: {
  plan: PublicPlanCard;
  lang: string;
}): JSX.Element {
  const { t } = useTranslation("saas");
  if (!plan.platform_fee_bps) {
    return (
      <>
        {t("pricingPlans.fee.none")}
        {plan.price_monthly_cents == null && (
          <>
            <br />
            <b>{t("pricingPlans.fee.quoted")}</b>
          </>
        )}
      </>
    );
  }
  const cap = plan.platform_fee_cap_monthly_minor;
  return (
    <>
      {t("pricingPlans.fee.rate", {
        percent: formatPercent(plan.platform_fee_bps, lang),
      })}
      <br />
      <b>
        {cap == null
          ? t("pricingPlans.fee.uncapped")
          : t("pricingPlans.fee.cap", {
              cap: formatMoney(cap, plan.platform_fee_cap_currency, lang),
            })}
      </b>
    </>
  );
}

// ----------------------------------------------------------------------
// Compare table — admin-managed metrics + entitlement rows grouped by
// EntitlementKey.group. Pure projection over /public/plans response.
// ----------------------------------------------------------------------

function CompareTable({
  plans,
  entitlements,
  metrics,
  billing,
  lang,
  vatNote,
}: {
  plans: PublicPlanCard[];
  entitlements: PublicEntitlementMeta[];
  metrics: PublicPlanMetric[];
  billing: BillingCycle;
  lang: string;
  vatNote: string;
}): JSX.Element {
  const { t } = useTranslation("saas");
  // KAN-1947 — on a phone the table shows one tier at a time (four columns
  // in 348 px cut Business and Enterprise off with no hint they existed).
  // Desktop ignores it; the CSS hides ``m-off`` cells only on narrow screens.
  const [picked, setPicked] = useState<string | null>(null);
  const active = picked ?? plans.find((p) => p.is_recommended)?.tier ?? plans[0]?.tier ?? "";
  const off = (tier: string): string | undefined => (tier === active ? undefined : "m-off");

  // Group entitlement rows (only is_public_listing) by their group;
  // metrics get their own pseudo-group so they can mix in the same
  // table layout.
  const sections = useMemo(() => {
    const out: { group: string; rows: CompareRow[] }[] = [];

    // Metric sections (admin-managed group string; null → "Limity").
    const metricGroups = new Map<string, PublicPlanMetric[]>();
    for (const m of [...metrics].sort(
      (a, b) =>
        a.sort_order - b.sort_order || a.display_name.localeCompare(b.display_name),
    )) {
      const g = m.group || t("pricingPlans.compareDefaultGroup");
      const arr = metricGroups.get(g) ?? [];
      arr.push(m);
      metricGroups.set(g, arr);
    }
    for (const [group, ms] of metricGroups) {
      out.push({
        group,
        rows: ms.map((m) => ({
          kind: "metric" as const,
          key: m.key,
          metric: m,
        })),
      });
    }

    // Entitlement sections. A row no *listed* tier grants renders "–" in
    // every column — it compares nothing and reads as a feature the
    // platform doesn't have. That is what "White-label e-maily" was: the
    // key is granted only by the hidden `enterprise` tier, so the public
    // table advertised a dead row (KAN-704). Drop it; it comes back the
    // moment a public plan grants the key.
    const grantedByAnyPlan = new Set(plans.flatMap((p) => p.default_entitlements));
    const visible = entitlements.filter(
      (e) => e.is_public_listing && grantedByAnyPlan.has(e.key),
    );
    const entGroups = new Map<string, PublicEntitlementMeta[]>();
    for (const e of visible) {
      const g = e.group || t("pricingPlans.compareUngrouped");
      const arr = entGroups.get(g) ?? [];
      arr.push(e);
      entGroups.set(g, arr);
    }
    for (const [group, es] of entGroups) {
      const rows = es
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map((e) => ({
          kind: "entitlement" as const,
          key: e.key,
          ent: e,
        }));
      // A group can hold limits and features alike (KAN-1948: the IT group
      // carries the API rate limit next to webhooks) — one section, not two.
      const existing = out.find((s) => s.group === group);
      if (existing) existing.rows.push(...rows);
      else out.push({ group, rows });
    }

    // The IT section goes last: everything above it is what a shop owner
    // decides on.
    return [
      ...out.filter((s) => s.group !== IT_GROUP),
      ...out.filter((s) => s.group === IT_GROUP),
    ];
  }, [entitlements, metrics, plans, t]);

  if (sections.length === 0) return <></>;

  const headerPrice = (p: PublicPlanCard): string => {
    const minor = monthlyMinor(p, billing);
    if (minor == null) return t("pricingPlans.contactPriceShort");
    const price = formatMoney(minor, p.currency, lang);
    return minor === 0 ? price : t("pricingPlans.perMonthShort", { price });
  };

  return (
    <section
      className="band"
      data-testid="pricing-plans-compare"
      aria-labelledby="pricing-compare-heading"
    >
      <div className="wrap">
        <SectionHead
          eyebrow={t("pricingPlans.compareEyebrow")}
          heading={t("pricingPlans.compareHeading")}
          lead={t("pricingPlans.compareLead")}
          headingId="pricing-compare-heading"
        />

        <div className="cmptabs" role="group" aria-label={t("pricingPlans.compareTabsLabel")}>
          {plans.map((p) => (
            <button
              key={p.tier}
              type="button"
              aria-pressed={p.tier === active}
              onClick={() => setPicked(p.tier)}
              data-testid={`pricing-compare-tab-${p.tier}`}
            >
              {pickI18nBlob(p.name_i18n, p.name, p.name_en, lang)}
            </button>
          ))}
        </div>
        <div className="cmp">
          <table className="cmp-t">
            <colgroup>
              <col className="cfeat" />
              {plans.map((p) => (
                <col key={p.tier} className={off(p.tier)} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{t("pricingPlans.featureColumn")}</th>
                {plans.map((p) => {
                  const name = pickI18nBlob(p.name_i18n, p.name, p.name_en, lang);
                  const cta = planCta(p, name, lang, t, true);
                  return (
                    <th
                      key={p.tier}
                      scope="col"
                      className={cx(p.is_recommended && "f", off(p.tier))}
                    >
                      {p.is_recommended && (
                        <span className="thbadge">{t("pricingPlans.compareBadge")}</span>
                      )}
                      <span className="pn">{name}</span>
                      <span className="thp">{headerPrice(p)}</span>
                      <Link
                        to={cta.to}
                        className={cx(
                          "btn btn-sm thcta",
                          p.is_recommended ? "light" : "btn-secondary",
                        )}
                      >
                        {cta.label}
                      </Link>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sections.map((section, i) => (
                <SectionRows
                  key={section.group}
                  index={i + 1}
                  // 🚨 ``section.group`` je ADMIN DATA a u entitlementů to je
                  // interní slug: ``auth``, ``b2b``, ``ops``, ``branding``.
                  // Vykreslený přímo publikoval vnitřní taxonomii jako
                  // nadpis sekce na první stránce, kterou platící zákazník
                  // otevře — přesně to, co KAN-704 opravil u JMEN řádků a
                  // u skupin přehlédl (změřeno na produkci 2026-09-06).
                  //
                  // Fallback na syrovou hodnotu je záměr: skupinu si zakládá
                  // admin, a schovat kvůli chybějícímu překladu celou sekci
                  // by bylo horší než ukázat slug. Že nějaký zůstal
                  // nepřeložený, hlásí sonda v ``infra/smoke.py``.
                  title={t(`pricingPlans.compareGroups.${section.group}`, {
                    defaultValue: section.group,
                  })}
                  rows={section.rows}
                  plans={plans}
                  lang={lang}
                  off={off}
                  collapsible={section.group === IT_GROUP}
                />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{t("pricingPlans.compareFootNote", { vat: vatNote }).trim()}</td>
                {plans.map((p) => {
                  const name = pickI18nBlob(p.name_i18n, p.name, p.name_en, lang);
                  const cta = planCta(p, name, lang, t, true);
                  return (
                    <td key={p.tier} className={cx(p.is_recommended && "f", off(p.tier))}>
                      <Link
                        to={cta.to}
                        className={cx(
                          "btn btn-sm",
                          p.is_recommended ? "btn-primary" : "btn-secondary",
                        )}
                      >
                        {cta.label}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="cmpfoot">
          <div className="legend">
            <span>
              <span className="yes" aria-hidden="true">
                <CheckIcon size={10} strokeWidth={3} />
              </span>{" "}
              {t("pricingPlans.legendIncluded")}
            </span>
            <span>
              <span className="no" aria-hidden="true" /> {t("pricingPlans.legendExcluded")}
            </span>
          </div>
          <Link to={`/${lang}/contact`}>{t("pricingPlans.compareContact")}</Link>
        </div>
      </div>
    </section>
  );
}

type CompareRow =
  | { kind: "metric"; key: string; metric: PublicPlanMetric }
  | { kind: "entitlement"; key: string; ent: PublicEntitlementMeta };

function SectionRows({
  index,
  title,
  rows,
  plans,
  lang,
  off,
  collapsible = false,
}: {
  index: number;
  title: string;
  rows: CompareRow[];
  plans: PublicPlanCard[];
  lang: string;
  off: (tier: string) => string | undefined;
  /** Folded until the visitor opens it (the IT section, KAN-1948 #47). */
  collapsible?: boolean;
}): JSX.Element {
  const { t } = useTranslation("saas");
  const [open, setOpen] = useState(!collapsible);
  return (
    <>
      <tr className={cx("grp", collapsible && "fold")}>
        <td data-n={String(index).padStart(2, "0")}>
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              data-testid="pricing-compare-it-toggle"
            >
              {title}
              <span className="foldhint">
                {t(open ? "pricingPlans.compareHideGroup" : "pricingPlans.compareShowGroup")}
              </span>
            </button>
          ) : (
            title
          )}
        </td>
        {plans.map((p) => (
          <td key={p.tier} className={cx(p.is_recommended && "f", off(p.tier))} />
        ))}
      </tr>
      {open && rows.map((row) => {
        const description =
          row.kind === "metric"
            ? row.metric.description
            : pickI18nBlob(row.ent.description_i18n, row.ent.description, row.ent.description_en, lang);
        return (
          <tr key={row.key}>
            <th scope="row">
              <div className="fname">
                {row.kind === "metric"
                  ? row.metric.display_name
                  : pickI18nBlob(row.ent.display_name_i18n, row.ent.display_name, row.ent.display_name_en, lang)}
                {row.kind === "entitlement" && !row.ent.is_wired && (
                  <span className="soon">{t("pricingPlans.comingSoon")}</span>
                )}
              </div>
              {description && <div className="fdesc">{description}</div>}
            </th>
            {plans.map((p) => (
              <td key={p.tier} className={cx("c", p.is_recommended && "f", off(p.tier))}>
                {row.kind === "metric" ? (
                  <MetricValue metric={row.metric} tier={p.tier} entitlements={p.default_entitlements} t={t} />
                ) : p.default_entitlements.includes(row.ent.key) ? (
                  <span
                    className="yes"
                    role="img"
                    aria-label={t("pricingPlans.legendIncluded")}
                  >
                    <CheckIcon size={12} strokeWidth={3} />
                  </span>
                ) : (
                  <span
                    className="no"
                    role="img"
                    aria-label={t("pricingPlans.legendExcluded")}
                  >
                    —
                  </span>
                )}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}

/**
 * A limit cell: the number, or the admin-chosen "unlimited" label for NULL.
 *
 * `unit` is a fixed suffix and that is wrong for a countable noun — Czech
 * declines it with the number ("1 den", "4 dny", "7 dní"), so a stored
 * "dní" printed "4 dní" on the public page. A metric whose unit declines
 * carries `unit_key` instead: an i18n key rendering number and noun
 * together through i18next's plural forms.
 */
function MetricValue({
  metric,
  tier,
  entitlements,
  t,
}: {
  metric: PublicPlanMetric;
  tier: string;
  entitlements: string[];
  t: T;
}): JSX.Element {
  const required = ({ max_webhooks: "webhooks", audit_retention_days: "audit_log" } as Record<string, string>)[metric.key];
  if (required && !entitlements.includes(required)) return <span>{t("pricingPlans.notIncluded")}</span>;
  const v = metric.values[tier];
  if (v == null) {
    return <span className="inf">{t(metric.unlimited_label_key)}</span>;
  }
  if (metric.unit_key) {
    return <>{t(metric.unit_key, { count: v })}</>;
  }
  // A rate unit ("/min") reads as one token with its number: "60/min".
  const gap = metric.unit.startsWith("/") ? "" : " ";
  return <>{metric.unit ? `${v}${gap}${metric.unit}` : String(v)}</>;
}
